import React, { useMemo, useState, useRef } from 'react';
import { MapContainer, GeoJSON, useMap, TileLayer } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

// ⚠️ adjust these paths to your project
import ddrGeoJson from './data/ddr.json';
import stateGeoJson from './data/state.json';
import indiaGeoJson from './data/india.json';
import { buildGeoIndex, resolveDdrToGeo } from './Ddrnames';
const INDIA_BOUNDS = [[6.5, 68.0], [36.0, 97.5]];

const MASK_STYLE = {
  fillColor: '#8A8A8A', fillOpacity: 0.55,
  color: 'transparent', weight: 0, interactive: false,
};
const OUTLINE_STYLE = {
  color: '#1A0005', weight: 2.5, opacity: 0.9,
  fill: false, lineJoin: 'round', lineCap: 'round', interactive: false,
};

/* Heat bands: KO coverage ÷ Campa coverage, where
     KO    = Aff KO@10  +  Aff KO@10 & Campa@10           (segments 1+2)
     Campa = Aff KO@10 & Campa@10 + Excl KO SSPET & Campa@10
             + Exclusive Campa@10                          (segments 2+3+4)
   Both are outlet counts, summed across the region before dividing —
   averaging per-DDR ratios would weight a tiny territory the same as a
   large one. */
const RATIO_BANDS = [
  { max: 0.5,      color: '#C42020', label: '< 0.5 — Campa ahead' },
  { max: 1.0,      color: '#E8B400', label: '0.5 – 1 — contested' },
  { max: Infinity, color: '#1A7A3A', label: '> 1 — KO ahead' },
];
const bandFor = (v) => RATIO_BANDS.find(b => v < b.max) || RATIO_BANDS[RATIO_BANDS.length - 1];

/* The name field differs between geojson sources */
const NAME_PROPS = ['Name', 'NAME_1', 'ST_NM', 'st_nm', 'STATE', 'State', 'name', 'NAME'];
const featureName = (f) => {
  const p = f?.properties || {};
  for (const k of NAME_PROPS) if (p[k]) return String(p[k]);
  return '';
};

function MapController({ onMapReady, bounds }) {
  const map = useMap();
  React.useEffect(() => {
    if (!map) return;
    onMapReady(map);
    if (bounds) map.fitBounds(bounds, { padding: [10, 10] });
  }, [map, onMapReady, bounds]);
  return null;
}

/**
 * kpiData rows are produced by DDRDashboard:
 *   { state, State, Month, segments: {label: value}, __affKo, __campa, ...numericKpis }
 * `segments` feeds the hover card; __affKo / __campa feed the ratio heat map.
 */
export default function MapDDRWithKpiHeatmap({ kpiData }) {
  const allKeys = Object.keys(kpiData[0] || {});
  const stateKey = 'state';
  const SKIP = new Set([stateKey, 'State', 'DDR', 'segments', '__koBase', '__campaBase']);

  const normalizeName = (n) => n?.toLowerCase().replace(/[\s_\-.]+/g, '').trim();

  const numericKeys = allKeys.filter(k => !SKIP.has(k) && typeof kpiData[0][k] === 'number');
  const nonNumericKeys = allKeys.filter(k => !SKIP.has(k) && typeof kpiData[0][k] !== 'number');

  const [level, setLevel] = useState('state');         // 'state' | 'ddr' — state by default
  const [colorBy, setColorBy] = useState('ratio');     // 'ratio' | 'value'
  const [selectedKpi, setSelectedKpi] = useState(numericKeys[0]);
  const [filters, setFilters] = useState(() => {
    const init = {};
    nonNumericKeys.forEach(k => { init[k] = 'ALL'; });
    return init;
  });
  const [showGeoLayer, setShowGeoLayer] = useState(false);
  const [spotlight, setSpotlight] = useState(true);
  const [hover, setHover] = useState(null);
  const mapRef = useRef(null);

  const geo = level === 'state' ? stateGeoJson : ddrGeoJson;

  const geoIndex = useMemo(() => buildGeoIndex(
    (ddrGeoJson.features || []).map(featureName).filter(Boolean)
  ), []);

  /* ── Inverse mask from the national outline ── */
  const maskFeature = useMemo(() => {
    const world = [[-180, -85], [180, -85], [180, 85], [-180, 85], [-180, -85]];
    const holes = [];
    const geoms = indiaGeoJson?.type === 'FeatureCollection'
      ? (indiaGeoJson.features || []).map(f => f?.geometry)
      : [indiaGeoJson?.geometry || indiaGeoJson];
    geoms.forEach((g) => {
      if (!g) return;
      if (g.type === 'Polygon') { if (g.coordinates?.[0]) holes.push(g.coordinates[0]); }
      else if (g.type === 'MultiPolygon') {
        (g.coordinates || []).forEach(poly => { if (poly?.[0]) holes.push(poly[0]); });
      }
    });
    if (!holes.length) console.warn('[map] india.json produced no rings — spotlight covers everything');
    return { type: 'Feature', properties: {},
      geometry: { type: 'Polygon', coordinates: [world, ...holes] } };
  }, []);

  const uniqueValues = useMemo(() => {
    const m = {};
    nonNumericKeys.forEach(k => { m[k] = new Set(kpiData.map(i => i[k])); });
    return m;
  }, [kpiData]);

  const filteredData = useMemo(() => kpiData.filter(item =>
    Object.entries(filters).every(([k, v]) => v === 'ALL' || item[k] === v)
  ), [kpiData, filters]);

  /* ── Aggregate onto the selected geography ──
     DDR level goes through the legacy-name resolver; state level matches the
     mapped State field straight against the state geojson. The ratio is
     recomputed from SUMMED parts — averaging ratios across regions of
     different size would be wrong. */
  const stats = useMemo(() => {
    const agg = {};
    let unresolved = 0;
    const bucket = (name) => (agg[name] = agg[name] || {
      name, value: 0, segments: {}, affKo: 0, campa: 0, rows: 0,
    });

    filteredData.forEach((entry) => {
      const targets = level === 'state'
        ? (entry.State ? [entry.State] : [])
        : resolveDdrToGeo(entry[stateKey], geoIndex);
      if (!targets.length) { unresolved++; return; }

      targets.forEach((t) => {
        const b = bucket(t);
        const v = Number(entry[selectedKpi]);
        if (!isNaN(v)) b.value += v;
        b.affKo += Number(entry.__koBase || 0);
        b.campa += Number(entry.__campaBase || 0);
        b.rows += 1;
        Object.entries(entry.segments || {}).forEach(([k, val]) => {
          b.segments[k] = (b.segments[k] || 0) + (Number(val) || 0);
        });
      });
    });

    const byName = {};
    const list = Object.values(agg).map((b) => {
      const rec = { ...b, ratio: b.campa > 0 ? b.affKo / b.campa : null };
      byName[normalizeName(b.name)] = rec;
      return rec;
    });

    const vals = list.map(l => l.value).filter(v => !isNaN(v));
    const sorted = [...list].sort((a, b) => b.value - a.value);
    if (unresolved) console.warn(`[map] ${unresolved} rows had no ${level} match`);

    return {
      byName, list,
      min: vals.length ? Math.min(...vals) : 0,
      max: vals.length ? Math.max(...vals) : 0,
      highest: sorted[0], lowest: sorted[sorted.length - 1],
      matched: list.length, unresolved,
    };
  }, [filteredData, selectedKpi, geoIndex, level]);

  const valueColor = (v) => {
    const range = (stats.max - stats.min) || 1;
    const t = Math.max(0, Math.min(1, (v - stats.min) / range));
    const r = t < 0.5 ? 255 : Math.round(255 - (t - 0.5) * 2 * 229);
    const g = t < 0.5 ? Math.round(t * 2 * 200) : Math.round(200 - (t - 0.5) * 2 * 78);
    return `rgb(${r}, ${g}, 40)`;
  };

  const getStyle = (feature) => {
    const rec = stats.byName[normalizeName(featureName(feature))];
    if (!rec) return { color: '#BBB', weight: 0.8, fillColor: '#E4E4E4', fillOpacity: 0.45 };
    const fillColor = colorBy === 'ratio'
      ? (rec.ratio == null ? '#D8D8D8' : bandFor(rec.ratio).color)
      : valueColor(rec.value);
    const isHover = hover && hover.name === rec.name;
    return {
      color: isHover ? '#000' : '#666',
      weight: isHover ? 2.5 : 1,
      fillColor,
      fillOpacity: showGeoLayer ? 0.62 : 0.88,
    };
  };

  const onEachFeature = (feature, layer) => {
    const name = featureName(feature);
    const rec = stats.byName[normalizeName(name)];
    const payload = rec || { name, segments: {}, ratio: null, value: null, rows: 0 };
    layer.on({
      mouseover: () => setHover(payload),
      mouseout: () => setHover(null),
      click: () => setHover(payload),
    });
  };

  const handleZoom = (d) => { if (mapRef.current) mapRef.current.setZoom(mapRef.current.getZoom() + d); };
  const resetView = () => { if (mapRef.current) mapRef.current.fitBounds(INDIA_BOUNDS, { padding: [10, 10] }); };

  const fmt = (v) => {
    if (v == null || isNaN(v)) return '—';
    if (Math.abs(v) >= 1000) return `${(v / 1000).toFixed(1)}K`;
    return String(Math.round(v));
  };

  if (!numericKeys.length && !kpiData.length) return null;

  return (
    <div style={{ zIndex: 0, border: '1px solid #d3d3d3', borderRadius: 8, padding: 12,
      display: 'flex', flexDirection: 'column', gap: 10 }}>

      {/* ── Controls ── */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', fontSize: 12 }}>
        <div style={{ display: 'inline-flex', border: '1px solid #ddd', borderRadius: 999, overflow: 'hidden' }}>
          {[['ddr', 'DDR map'], ['state', 'State map']].map(([id, lab]) => (
            <button key={id} onClick={() => setLevel(id)} style={{
              padding: '4px 14px', border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 700,
              background: level === id ? '#E8001A' : '#fff', color: level === id ? '#fff' : '#777' }}>{lab}</button>
          ))}
        </div>

        <div style={{ display: 'inline-flex', border: '1px solid #ddd', borderRadius: 999, overflow: 'hidden' }}>
          {[['ratio', 'KO/Campa ratio'], ['value', 'KPI value']].map(([id, lab]) => (
            <button key={id} onClick={() => setColorBy(id)} style={{
              padding: '4px 12px', border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 600,
              background: colorBy === id ? '#1A0005' : '#fff', color: colorBy === id ? '#fff' : '#777' }}>{lab}</button>
          ))}
        </div>

        {colorBy === 'value' && (
          <select value={selectedKpi} onChange={e => setSelectedKpi(e.target.value)} style={ctrl}>
            {numericKeys.map(k => <option key={k} value={k}>{k}</option>)}
          </select>
        )}

        {nonNumericKeys.map(k => (
          <span key={k} style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
            <label style={{ fontWeight: 600, fontSize: 11 }}>{k}:</label>
            <select value={filters[k] || 'ALL'} onChange={e => setFilters({ ...filters, [k]: e.target.value })} style={ctrl}>
              <option value="ALL">ALL</option>
              {[...(uniqueValues[k] || [])].map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </span>
        ))}

        <button onClick={() => setSpotlight(p => !p)} style={{ ...pill,
          borderColor: spotlight ? '#E8001A' : '#ddd', background: spotlight ? '#FFF0F0' : '#fff',
          color: spotlight ? '#E8001A' : '#666' }}>◉ Spotlight</button>
        <button onClick={() => setShowGeoLayer(p => !p)} style={{ ...pill,
          borderColor: showGeoLayer ? '#E8001A' : '#ddd', background: showGeoLayer ? '#FFF0F0' : '#fff',
          color: showGeoLayer ? '#E8001A' : '#666' }}>🗺 Base map</button>
        <button onClick={() => handleZoom(0.5)} style={zoomBtn}>+</button>
        <button onClick={() => handleZoom(-1)} style={zoomBtn}>−</button>
        <button onClick={resetView} style={{ ...zoomBtn, width: 'auto', padding: '0 8px' }}>⟲</button>
      </div>

      {/* ── Legend ── */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, fontSize: 10.5, alignItems: 'center' }}>
        {colorBy === 'ratio' ? (
          <>
            <b style={{ fontSize: 10, color: '#5C1020' }}>KO coverage ÷ Campa coverage</b>
            {RATIO_BANDS.map(b => (
              <span key={b.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: '#777' }}>
                <span style={{ width: 12, height: 12, borderRadius: 3, background: b.color }} />{b.label}
              </span>
            ))}
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: '#777' }}>
              <span style={{ width: 12, height: 12, borderRadius: 3, background: '#D8D8D8' }} />no Campa
            </span>
          </>
        ) : (
          <div style={{ minWidth: 220 }}>
            <div style={{ height: 12, borderRadius: 4,
              background: 'linear-gradient(to right, rgb(255,0,40), rgb(255,200,40), rgb(26,122,40))' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9.5, marginTop: 2 }}>
              <span>{fmt(stats.min)}</span><span>{selectedKpi}</span><span>{fmt(stats.max)}</span>
            </div>
          </div>
        )}
        <span style={{ marginLeft: 'auto', fontSize: 10, color: '#8A7070' }}>
          {stats.matched} / {(geo.features || []).length} {level === 'state' ? 'states' : 'DDRs'} mapped
          {stats.unresolved ? ` · ${stats.unresolved} unmatched` : ''}
        </span>
      </div>

      {/* ── Map + hover card ── */}
      <div style={{ position: 'relative' }}>
        <MapContainer
          center={[22.9734, 78.6569]} zoom={4}
          style={{ zIndex: 0, borderRadius: 8, border: '1px solid #ddd',
            height: 520, width: '100%', background: '#F4F4F4' }}
          zoomControl={false} scrollWheelZoom={false} doubleClickZoom={false}
          boxZoom={false} keyboard={false} dragging touchZoom={false}>

          <MapController onMapReady={(m) => { mapRef.current = m; }} bounds={INDIA_BOUNDS} />

          {showGeoLayer && (
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              zIndex={1} />
          )}

          <GeoJSON key={`${level}-${colorBy}-${selectedKpi}-${JSON.stringify(filters)}`}
            data={geo} style={getStyle} onEachFeature={onEachFeature} />

          {spotlight && (
            <GeoJSON key="india-mask" data={maskFeature} style={() => MASK_STYLE} interactive={false} />
          )}

          <GeoJSON key="india-outline" data={indiaGeoJson} style={() => OUTLINE_STYLE} interactive={false} />
        </MapContainer>

        {/* Hover card — the full five-way segment split */}
        {hover && (
          <div style={{
            position: 'absolute', top: 12, right: 12, zIndex: 500, minWidth: 252,
            background: 'rgba(255,255,255,0.97)', border: '1px solid #F5BABA',
            borderRadius: 10, padding: '12px 14px', boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
            fontSize: 11.5, pointerEvents: 'none' }}>
            <div style={{ fontWeight: 700, color: '#1A0005', fontSize: 13 }}>{hover.name}</div>
            <div style={{ fontSize: 9.5, color: '#9A5060', textTransform: 'uppercase',
              letterSpacing: 0.8, marginBottom: 8 }}>
              {level === 'state' ? 'State' : 'DDR'}{hover.rows > 1 ? ` · ${hover.rows} DDRs` : ''}
            </div>

            {Object.keys(hover.segments || {}).length === 0 ? (
              <div style={{ color: '#9A5060' }}>No data for this region</div>
            ) : (
              <>
                {Object.entries(hover.segments).map(([k, v]) => (
                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, marginBottom: 3 }}>
                    <span style={{ color: '#5C1020' }}>{k}</span>
                    <b style={{ color: '#1A0005' }}>{fmt(v)}</b>
                  </div>
                ))}
                <div style={{ borderTop: '1px solid #FADDDD', marginTop: 8, paddingTop: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, marginBottom: 3 }}>
                    <span style={{ color: '#5C1020' }}>KO coverage <span style={{ fontSize: 9, color: '#9A5060' }}>(1+2)</span></span>
                    <b style={{ color: '#1A0005' }}>{fmt(hover.affKo)}</b>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, marginBottom: 6 }}>
                    <span style={{ color: '#5C1020' }}>Campa coverage <span style={{ fontSize: 9, color: '#9A5060' }}>(2+3+4)</span></span>
                    <b style={{ color: '#1A0005' }}>{fmt(hover.campa)}</b>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16,
                    borderTop: '1px solid #FADDDD', paddingTop: 6 }}>
                    <span style={{ color: '#5C1020', fontWeight: 600 }}>KO ÷ Campa</span>
                    <b style={{ color: hover.ratio == null ? '#9A5060' : bandFor(hover.ratio).color }}>
                      {hover.ratio == null ? 'no Campa' : `${hover.ratio.toFixed(2)}×`}
                    </b>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const ctrl = { border: '1px solid #ddd', borderRadius: 6, padding: '3px 8px', fontSize: 11 };
const pill = { border: '1px solid #ddd', borderRadius: 999, padding: '4px 10px',
  fontSize: 11, cursor: 'pointer', fontWeight: 600 };
const zoomBtn = { border: '1px solid #ddd', borderRadius: 6, width: 26, height: 26,
  cursor: 'pointer', background: '#fff', fontWeight: 700 };